import React, { useState, useEffect } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { FaUtensils, FaDumbbell, FaQuestionCircle, FaSun, FaMoon } from 'react-icons/fa';
import { toast } from 'react-hot-toast';
import pendingActionService from '../services/pendingActionService';
import cartService from '../services/cartService';
import guestCartService from '../services/guestCartService';
import { trackSignupStart, trackSignupComplete, trackLogin, identifyUser } from '../utils/analytics';
import { getPostAuthRedirect } from '../utils/authRedirect';
import useGoogleOneTapSafePosition from '../hooks/useGoogleOneTapSafePosition';
import useWindowSize from '../hooks/useWindowSize';
import { getNavbarGoogleLoginProps, isNavbarGoogleIcon } from '../utils/googleSignInButton';
import './Navbar.css';

const Navbar = () => {
  const [authError, setAuthError] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [desktopSections, setDesktopSections] = useState({ solutions: true, meals: true, fitness: true, admin: true, business: true, help: true });
  const [mobileSections, setMobileSections] = useState({ solutions: true, meals: true, fitness: true, admin: true, business: true, help: true });
  const [theme, setTheme] = useState(() => localStorage.getItem('siteTheme') || 'light');
  const [cartCount, setCartCount] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const { width: viewportWidth } = useWindowSize();
  const navbarGoogleLoginProps = getNavbarGoogleLoginProps(viewportWidth);
  const navbarGoogleIsIcon = isNavbarGoogleIcon(viewportWidth);
  useGoogleOneTapSafePosition();
  const userString = localStorage.getItem('user');
  const user = userString ? JSON.parse(userString) : null;

  // Fetch cart count for all users (authenticated uses server, guest uses localStorage)
  useEffect(() => {
    if (user && localStorage.getItem('token')) {
      cartService.getCart()
        .then(data => setCartCount((data.menuEntries || []).length))
        .catch(() => {});
    } else {
      setCartCount(guestCartService.getCartCount());
    }

    // Listen for guest cart updates
    const handleGuestCartUpdate = () => {
      if (!localStorage.getItem('token')) {
        setCartCount(guestCartService.getCartCount());
      }
    };
    window.addEventListener('guestCartUpdated', handleGuestCartUpdate);
    return () => window.removeEventListener('guestCartUpdated', handleGuestCartUpdate);
  }, [location.pathname]); // Re-fetch when navigating // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    pendingActionService.clearPendingToken(); // Clean up any pending actions
    navigate('/');
    window.location.reload();
  };

  const toggleMenu = () => {
    setIsMenuOpen(prev => !prev);
  };

  // Close mobile menu on scroll or outside click
  useEffect(() => {
    if (!isMenuOpen) return;

    const handleScroll = () => setIsMenuOpen(false);
    const handleClickOutside = (e) => {
      // Close if click is outside the mobile menu and hamburger button
      if (!e.target.closest('.mobile-menu') && !e.target.closest('.hamburger-menu')) {
        setIsMenuOpen(false);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isMenuOpen]);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('siteTheme', theme);
  }, [theme]);

  const handleGoogleSuccess = async (credentialResponse) => {
    trackSignupStart('navbar');
    try {
      if (!credentialResponse.credential) {
        setAuthError('Missing authentication credentials');
        return;
      }

      const apiUrl = `${process.env.REACT_APP_API_URL}/auth/google`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ 
          credential: credentialResponse.credential 
        }),
      });

      if (!response.ok) {
        throw new Error(`Authentication failed: ${response.status}`);
      }

      const data = await response.json();
      
      const userData = {
        ...data.user,
        sub: data.user.sub || data.user.id || data.user.email
      };
      
      console.log('[Navbar] Login successful, storing user data');
      
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('token', data.token);

      const userId = userData.sub || userData.email;
      if (data.isNewUser) {
        trackSignupComplete('navbar', userId);
      } else {
        trackLogin('navbar', userId);
      }
      identifyUser(userId, { email: userData.email });

      const redirectUrl = await pendingActionService.checkAndExecutePendingAction();
      const next = getPostAuthRedirect({
        isNewUser: data.isNewUser,
        pendingRedirectUrl: redirectUrl,
        pendingSubscription: localStorage.getItem('pendingSubscription'),
        defaultHref: null
      });

      if (next) {
        if (redirectUrl) {
          toast.success('Successfully connected with your coach!');
        }
        window.location.href = next;
      } else {
        window.location.reload();
      }
      
    } catch (error) {
      console.error('Auth error:', error);
      setAuthError(`Login failed: ${error.message}`);
    }
  };

  // Top-level "Solutions" group surfaces the persona landing pages for SEO
  // entry points and to give first-time visitors a clear top-of-funnel path.
  const solutionsNav = [
    { path: '/meal-planning', label: 'Meal Planning' },
    { path: '/fitness-training', label: 'Fitness Training' },
    { path: '/coaching', label: 'Coaching' },
    { path: '/for-business', label: 'For Business' },
  ];

  // Navigation items grouped by section
  const mealPlanningNav = [
    { path: '/menu', label: 'Menu' },
    { path: '/meal-plan', label: 'Meal Plan' },
    { path: '/nutrition/planner', label: 'Nutrition' },
    { path: '/cart', label: 'Cart', cartBadge: true },
    { path: '/creators', label: 'Creators' },
    { path: '/my-menus', label: 'My Menus', hideForAdmin: true, hideForAffiliate: true },
    { path: '/community-kitchen', label: 'Community Kitchen', requiresAuth: true },
  ];

  const fitnessNav = [
    { path: '/fitness', label: 'Programs' },
    { path: '/fitness/workouts', label: 'Workouts' },
    { path: '/fitness/exercises', label: 'Exercises' },
    { path: '/coaches', label: 'Coaches' },
    { path: '/fitness/my-workouts', label: 'My Workouts' },
  ];

  const hasNutrition = user?.nutritionEnabled ?? (user?.isAffiliate ? user?.specialization !== 'fitness' : false);
  const hasFitness = user?.fitnessEnabled ?? (user?.isAffiliate ? user?.specialization !== 'nutrition' : false);

  const getVisibleLinks = (items) => items.filter((item) => {
    if (item.hideForAdmin && user?.isAdmin) return false;
    if (item.hideForAffiliate && user?.isAffiliate) return false;
    if (item.requiresAuth && !user) return false;
    return true;
  });

  const solutionLinks = solutionsNav;

  const mealLinks = getVisibleLinks([
    ...mealPlanningNav,
    ...(user?.isAffiliate && hasNutrition ? [{ path: '/affiliate/dashboard', label: 'Creator Dashboard' }] : []),
  ]);

  const fitnessLinks = getVisibleLinks([
    ...fitnessNav,
    ...(user?.isAffiliate && (hasFitness || hasNutrition)
      ? [{ path: '/coach/dashboard', label: 'Coach Dashboard' }]
      : []),
  ]);

  const adminLinks = user?.isAdmin ? [
    { path: '/admin/dashboard', label: 'Admin Dashboard' },
    { path: '/admin/support', label: 'Support Tickets' },
  ] : [];

  // Bottom link group: Pricing is always visible (it must be discoverable
  // for anonymous visitors too); Support only makes sense once logged in.
  const supportLinks = [
    { path: '/pricing', label: 'Pricing' },
    ...(user ? [{ path: '/support', label: 'Support' }] : []),
  ];

  const businessLinks = [
    ...(user?.isBusinessOwner ? [{ path: '/business/dashboard', label: 'Business Dashboard' }] : []),
    ...(user?.isBusinessEmployee && !user?.isBusinessOwner ? [{ path: '/business/employee/dashboard', label: 'Employee Dashboard' }] : []),
  ];

  const toggleSection = (stateSetter, section) => {
    stateSetter((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const renderLinkGroup = (items, isMobile = false) => {
    const onClick = isMobile ? () => setIsMenuOpen(false) : undefined;

    return items.map((item) => (
      <Link
        key={item.path}
        to={item.path}
        onClick={onClick}
        className={`${location.pathname === item.path ? 'active' : ''} ${item.cartBadge ? 'nav-cart-link' : ''}`.trim()}
      >
        {item.label}
        {item.cartBadge && cartCount > 0 && <span className="nav-cart-badge">{cartCount}</span>}
      </Link>
    ));
  };

  const renderSection = ({ title, icon, items, expanded, onToggle, isMobile = false }) => {
    if (!items.length) return null;

    return (
      <div className={`nav-section ${isMobile ? 'mobile-nav-section' : 'desktop-nav-section'}`}>
        <button
          type="button"
          className={`nav-section-toggle ${expanded ? 'expanded' : ''}`}
          onClick={onToggle}
        >
          <span className="nav-section-title-wrap">
            <span className="nav-section-icon">{icon}</span>
            <span className="nav-section-title">{title}</span>
          </span>
          <span className="nav-section-chevron">{expanded ? '−' : '+'}</span>
        </button>
        {expanded && (
          <div className={`nav-section-links ${isMobile ? 'mobile-section-links' : 'desktop-section-links'}`}>
            {renderLinkGroup(items, isMobile)}
          </div>
        )}
      </div>
    );
  };

  const renderThemeToggle = (isMobile = false) => (
    <div className={`theme-toggle ${isMobile ? 'theme-toggle-mobile' : 'theme-toggle-desktop'}`}>
      <span className="theme-toggle-label">Theme</span>
      <div className="theme-toggle-buttons">
        <button
          type="button"
          className={`theme-toggle-button ${theme === 'light' ? 'active' : ''}`}
          onClick={() => setTheme('light')}
          aria-pressed={theme === 'light'}
          title="Light mode"
        >
          <FaSun />
          <span>Light</span>
        </button>
        <button
          type="button"
          className={`theme-toggle-button ${theme === 'dark' ? 'active' : ''}`}
          onClick={() => setTheme('dark')}
          aria-pressed={theme === 'dark'}
          title="Dark mode"
        >
          <FaMoon />
          <span>Dark</span>
        </button>
      </div>
    </div>
  );

  return (
    <nav className="navbar">
      <div className="navbar-container">
        <button className="hamburger-menu" onClick={toggleMenu}>
          <span className={`hamburger-line ${isMenuOpen ? 'open' : ''}`}></span>
          <span className={`hamburger-line ${isMenuOpen ? 'open' : ''}`}></span>
          <span className={`hamburger-line ${isMenuOpen ? 'open' : ''}`}></span>
        </button>

        <div className="brand-section">
          <Link to={'/'} className="site-title">
            FreshFitLab
          </Link>
        </div>

        <div className={`navbar-auth-section${user ? '' : ' navbar-auth-section--guest'}`}>
          {user ? (
            <div className="user-info">
              <img 
                src={user.picture || 'default-avatar.png'} 
                alt={user.name} 
                className="user-avatar"
                onClick={() => navigate('/profile')}
                style={{ cursor: 'pointer' }}
                title="My Profile"
              />
              <span className="user-name" onClick={() => navigate('/profile')} style={{ cursor: 'pointer' }} title="My Profile">{user.name}</span>
              <button onClick={handleLogout} className="logout-button">
                Logout
              </button>
            </div>
          ) : (
            <div className="login-buttons">
              {process.env.NODE_ENV === 'development' && (
                <button
                  onClick={() => navigate('/dev/login')}
                  className="dev-login-btn"
                >
                  Dev Login
                </button>
              )}
              <div
                className={`google-login-button${navbarGoogleIsIcon ? ' google-login-button--icon' : ''}`}
                data-testid="navbar-google-login"
              >
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={() => setAuthError('Login Failed')}
                  useOneTap
                  {...navbarGoogleLoginProps}
                />
              </div>
            </div>
          )}
        </div>
      </div>

        {isMenuOpen && <div className="mobile-menu-backdrop" />}
        <div className={`mobile-menu ${isMenuOpen ? 'open' : ''}`}>
          <div className="mobile-menu-header">
            <span className="mobile-nav-label">Browse</span>
          </div>
          <div className="mobile-nav-groups">
            {renderSection({
              title: 'Solutions',
              icon: '◆',
              items: solutionLinks,
              expanded: mobileSections.solutions,
              onToggle: () => toggleSection(setMobileSections, 'solutions'),
              isMobile: true,
            })}
            {renderSection({
              title: 'Meals',
              icon: <FaUtensils />,
              items: mealLinks,
              expanded: mobileSections.meals,
              onToggle: () => toggleSection(setMobileSections, 'meals'),
              isMobile: true,
            })}
            {renderSection({
              title: 'Fitness',
              icon: <FaDumbbell />,
              items: fitnessLinks,
              expanded: mobileSections.fitness,
              onToggle: () => toggleSection(setMobileSections, 'fitness'),
              isMobile: true,
            })}
            {renderSection({
              title: 'Admin',
              icon: '•',
              items: adminLinks,
              expanded: mobileSections.admin,
              onToggle: () => toggleSection(setMobileSections, 'admin'),
              isMobile: true,
            })}
            {renderSection({
              title: 'Business',
              icon: '•',
              items: businessLinks,
              expanded: mobileSections.business,
              onToggle: () => toggleSection(setMobileSections, 'business'),
              isMobile: true,
            })}
            {renderSection({
              title: 'Help',
              icon: <FaQuestionCircle />,
              items: supportLinks,
              expanded: mobileSections.help,
              onToggle: () => toggleSection(setMobileSections, 'help'),
              isMobile: true,
            })}
          </div>
          {renderThemeToggle(true)}
          {process.env.NODE_ENV === 'development' && !user && (
            <button
              type="button"
              className="dev-login-btn"
              onClick={() => {
                setIsMenuOpen(false);
                navigate('/dev/login');
              }}
            >
              Dev Login
            </button>
          )}
        </div>

      <aside className="desktop-sidebar" aria-label="Primary navigation">
        <div className="desktop-sidebar-inner">
          {renderSection({
            title: 'Solutions',
            icon: '◆',
            items: solutionLinks,
            expanded: desktopSections.solutions,
            onToggle: () => toggleSection(setDesktopSections, 'solutions'),
          })}
          {renderSection({
            title: 'Meals',
            icon: <FaUtensils />,
            items: mealLinks,
            expanded: desktopSections.meals,
            onToggle: () => toggleSection(setDesktopSections, 'meals'),
          })}
          {renderSection({
            title: 'Fitness',
            icon: <FaDumbbell />,
            items: fitnessLinks,
            expanded: desktopSections.fitness,
            onToggle: () => toggleSection(setDesktopSections, 'fitness'),
          })}
          {renderSection({
            title: 'Admin',
            icon: '•',
            items: adminLinks,
            expanded: desktopSections.admin,
            onToggle: () => toggleSection(setDesktopSections, 'admin'),
          })}
          {renderSection({
            title: 'Business',
            icon: '•',
            items: businessLinks,
            expanded: desktopSections.business,
            onToggle: () => toggleSection(setDesktopSections, 'business'),
          })}
          {renderSection({
            title: 'Help',
            icon: <FaQuestionCircle />,
            items: supportLinks,
            expanded: desktopSections.help,
            onToggle: () => toggleSection(setDesktopSections, 'help'),
          })}
        </div>
        <div className="desktop-sidebar-footer">
          {renderThemeToggle(false)}
        </div>
      </aside>
    </nav>
  );
};

export default Navbar;
